import {
  ReactElement, useCallback, useEffect, useMemo, useState,
} from 'react';
import { BigNumbersContainer } from './styles';
import { useTranslation } from 'react-i18next';
import { useFilterContext } from '../contexts/FilterContext';
import { GetOverviewSummary } from '../types';
import { getOverviewSummary } from '../helpers/overview';
import { useToast } from 'contexts/newToastContext';
import { Units } from './Units';
import { Healthy } from './Healthy';
import { UnitsDisponibility } from './UnitsDisponibility';
import { HvacEnergy } from './HvacEnergy';
import { ThermalComfort } from './ThermalComfort';

interface BigNumbersProps {
  hasHealth: boolean;
  hasThermalComfort: boolean;
  loading: boolean;
}

export function BigNumbers({
  hasHealth,
  hasThermalComfort,
  loading,
}: Readonly<BigNumbersProps>): ReactElement {
  const { selectedOptions, clients, filtersDependencies } = useFilterContext();
  const { t } = useTranslation();
  const { toast } = useToast();

  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryData, setSummaryData] = useState<GetOverviewSummary | undefined>();

  const generalLoading = useMemo(() => loading || loadingSummary, [loading, loadingSummary]);

  const getSummaryInfo = useCallback(async () => {
    const clientIds = clients.map(Number);
    setLoadingSummary(true);

    const data = await getOverviewSummary(clientIds, {
      stateIds: selectedOptions.state,
      unitIds: selectedOptions.units?.map((item) => Number(item.value)) ?? selectedOptions.group?.units,
      healthStatus: selectedOptions.healthStatus,
      thermalComfortStatus: selectedOptions.status,
      cityIds: selectedOptions.cities?.map((item) => item.value),
    });

    if (data) {
      setSummaryData(data);
    } else {
      toast(t('erroTitulo'), t('erroBuscaDadosContabilizacao'), 'error');
    }

    setLoadingSummary(false);
  }, [filtersDependencies]);

  useEffect(() => {
    if (clients && clients.length > 0) {
      getSummaryInfo();
    }
  }, [filtersDependencies]);

  return (
    <BigNumbersContainer>
      <Units
        data={summaryData?.UNITS}
        loading={generalLoading}
      />
      <Healthy
        hasHealth={hasHealth}
        loading={loading}
      />
      <UnitsDisponibility
        data={summaryData?.DISPONIBILITY}
        loading={generalLoading}
      />
      <ThermalComfort hasThermalComfort={hasThermalComfort} loading={generalLoading} />
      <HvacEnergy loading={generalLoading} />
    </BigNumbersContainer>
  );
}
